/**
 * CreateSubscriptionModal - Modal for creating subscriptions for Andes tables
 * @module components/starnexus/andes/CreateSubscriptionModal
 */

import React, { useState, useEffect, useMemo } from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Multiselect from '@amzn/awsui-components-console/multiselect';
import Select from '@amzn/awsui-components-console/select';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import Textarea from '@amzn/awsui-components-console/textarea';
import Alert from '@amzn/awsui-components-console/alert';
import type { MultiselectProps } from '@amzn/awsui-components-console/multiselect';
import type { SelectProps } from '@amzn/awsui-components-console/select';
import type { RadioGroupProps } from '@amzn/awsui-components-console/radio-group';
import type { CreateSubscriptionInput, SubscriptionTarget, VersionMigrationStrategy, SubscriptionTargetType } from '../../../types/starnexus/andesAccess';
import { AWS_REGIONS } from '../../../types/starnexus/andesAccess';
import { mockAndesAccessService } from '../../../services/starnexus/mock/mockAndesAccessService';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

export interface CreateSubscriptionModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (input: CreateSubscriptionInput) => Promise<void>;
  preselectedDatasetId?: string;
}

// Version migration strategy options
const VERSION_MIGRATION_OPTIONS: RadioGroupProps.RadioButtonDefinition[] = [
  {
    value: 'AUTO',
    label: 'AUTO (Recommended)',
    description: 'Automatically migrate to new table versions when available'
  },
  {
    value: 'MANUAL',
    label: 'MANUAL',
    description: 'Manually control when to migrate to new table versions'
  }
];

// Region options from AWS_REGIONS constant
const REGION_OPTIONS: SelectProps.Option[] = AWS_REGIONS.map((r) => ({
  label: r.label,
  value: r.value
}));

export const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({ visible, onDismiss, onSubmit, preselectedDatasetId }) => {
  // Form state
  const [selectedDatasets, setSelectedDatasets] = useState<MultiselectProps.Option[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<SelectProps.Option | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<SelectProps.Option | null>(null);
  const [versionMigrationStrategy, setVersionMigrationStrategy] = useState<VersionMigrationStrategy>('AUTO');
  const [description, setDescription] = useState('');

  // Data state
  const [allDatasets, setAllDatasets] = useState<any[]>([]);
  const [subscriptionTargets, setSubscriptionTargets] = useState<SubscriptionTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load datasets from API
  useEffect(() => {
    apiFetch(apiUrl('/api/datasets'), { ...apiFetchDefaults }).then(r => r.json()).then(setAllDatasets).catch(() => {});
  }, []);

  // Get datasets with Andes configuration
  const datasetsWithAndes = useMemo(() => {
    return allDatasets.filter((d: any) => d.config?.andesSettings?.provider && d.config?.andesSettings?.tableName);
  }, [allDatasets]);

  // Convert datasets to multiselect options
  const datasetOptions: MultiselectProps.Option[] = useMemo(() => {
    return datasetsWithAndes.map((d) => ({
      label: d.datasetName,
      value: d.datasetId,
      description: `${d.config?.andesSettings?.provider} / ${d.config?.andesSettings?.tableName}`
    }));
  }, [datasetsWithAndes]);

  // Group subscription targets by type for the dropdown
  const targetOptions: SelectProps.OptionGroup[] = useMemo(() => {
    const grouped: Record<SubscriptionTargetType, SelectProps.Option[]> = {
      DATASHARE: [],
      CRADLE: [],
      GLUE: []
    };

    subscriptionTargets.forEach((target) => {
      grouped[target.targetType].push({
        label: target.targetName,
        value: target.targetId,
        description: target.description,
        labelTag: target.targetType
      });
    });

    return [
      { label: 'DATASHARE', options: grouped.DATASHARE },
      { label: 'CRADLE', options: grouped.CRADLE },
      { label: 'GLUE', options: grouped.GLUE }
    ].filter((group) => group.options.length > 0);
  }, [subscriptionTargets]);

  // Load subscription targets when modal opens
  useEffect(() => {
    if (visible) {
      setLoadingTargets(true);
      mockAndesAccessService
        .getSubscriptionTargets()
        .then((targets) => {
          setSubscriptionTargets(targets);
        })
        .catch((err) => {
          setError(`Failed to load subscription targets: ${err.message}`);
        })
        .finally(() => {
          setLoadingTargets(false);
        });
    }
  }, [visible]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      setError(null);
      setSubmitting(false);

      // Pre-select dataset if provided
      if (preselectedDatasetId) {
        const dataset = datasetsWithAndes.find((d) => d.datasetId === preselectedDatasetId);
        if (dataset) {
          setSelectedDatasets([
            {
              label: dataset.datasetName,
              value: dataset.datasetId,
              description: `${dataset.config?.andesSettings?.provider} / ${dataset.config?.andesSettings?.tableName}`
            }
          ]);
        }
      } else {
        setSelectedDatasets([]);
      }

      setSelectedTarget(null);
      setSelectedRegion(null);
      setVersionMigrationStrategy('AUTO');
      setDescription('');
    }
  }, [visible, preselectedDatasetId, datasetsWithAndes]);

  // Form validation
  const isFormValid = useMemo(() => {
    return selectedDatasets.length > 0 && selectedTarget !== null && selectedRegion !== null;
  }, [selectedDatasets, selectedTarget, selectedRegion]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || !selectedTarget || !selectedRegion) return;

    setSubmitting(true);
    setError(null);

    try {
      const input: CreateSubscriptionInput = {
        datasetIds: selectedDatasets.map((d) => d.value!),
        targetId: selectedTarget.value!,
        region: selectedRegion.value!,
        versionMigrationStrategy,
        description: description.trim() || undefined
      };

      await onSubmit(input);
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Create subscription"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={!isFormValid || submitting}>
              {selectedDatasets.length > 1 ? `Create subscriptions (${selectedDatasets.length} tables)` : 'Create subscription'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedDatasets.length > 1 && <Alert type="info">You are creating subscriptions for {selectedDatasets.length} tables. A separate subscription will be created for each table.</Alert>}

        <FormField label="Tables" description="Select one or more datasets with Andes configuration" constraintText={`${datasetOptions.length} datasets available`}>
          <Multiselect
            selectedOptions={selectedDatasets}
            onChange={({ detail }) => setSelectedDatasets(detail.selectedOptions as MultiselectProps.Option[])}
            options={datasetOptions}
            placeholder="Select datasets"
            filteringType="auto"
            tokenLimit={3}
            disabled={submitting}
          />
        </FormField>

        <FormField label="Subscription target" description="Select the destination where the table data will be available">
          <Select
            selectedOption={selectedTarget}
            onChange={({ detail }) => setSelectedTarget(detail.selectedOption)}
            options={targetOptions}
            placeholder={loadingTargets ? 'Loading targets...' : 'Select subscription target'}
            disabled={submitting || loadingTargets}
            filteringType="auto"
          />
        </FormField>

        <FormField label="Region" description="AWS region where the subscription will be created">
          <Select selectedOption={selectedRegion} onChange={({ detail }) => setSelectedRegion(detail.selectedOption)} options={REGION_OPTIONS} placeholder="Select region" disabled={submitting} />
        </FormField>

        <FormField label="Version migration strategy" description="How the subscription handles table version updates">
          <RadioGroup value={versionMigrationStrategy} onChange={({ detail }) => setVersionMigrationStrategy(detail.value as VersionMigrationStrategy)} items={VERSION_MIGRATION_OPTIONS} />
        </FormField>

        <FormField
          label={
            <span>
              Description <i>- optional</i>
            </span>
          }
          description="Add notes about this subscription"
        >
          <Textarea value={description} onChange={({ detail }) => setDescription(detail.value)} placeholder="Enter a description for this subscription..." rows={3} disabled={submitting} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default CreateSubscriptionModal;
